import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import BrowseTrips from "@/components/BrowseTrips";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, Package, MapPin, CalendarDays, Loader2 } from "lucide-react";
import { sizeLabel } from "@/lib/sizes";
import ContactButtons from "@/components/ContactButtons";

const REQ_LABEL = {
  requested: "Waiting for reply", expired: "Expired", declined: "Declined", withdrawn: "Withdrawn",
  declined_other_accepted: "Cancelled — another carrier accepted", awaiting_payment: "Accepted · awaiting payment",
  accepted: "Accepted", picked_up: "Picked up", in_transit: "In transit", delivered: "Delivered",
  cancelled: "Cancelled", payment_timeout: "Payment window expired",
};

const OPEN = new Set(["draft", "requested"]);

const Info = ({ icon: Icon, children, testid }) => (
  <div className="flex items-start gap-2 text-sm text-slate-700" data-testid={testid}>
    <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0"/> <div className="min-w-0">{children}</div>
  </div>
);

const RequestHistory = ({ items }) => {
  if (!items.length) return <div className="text-sm text-slate-500" data-testid="no-requests">No requests sent yet.</div>;
  return (
    <div className="divide-y divide-slate-100">
      {items.map((d) => (
        <div key={d.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`parcel-request-${d.id}`}>
          <div className="min-w-0">
            <div className="font-medium truncate">{d.carrier?.name || "Carrier"}{d.initiated_by === "carrier" ? " · offered" : ""}</div>
            <div className="text-[11px] text-slate-500">{new Date(d.created_at).toLocaleString()}</div>
          </div>
          <Badge className="bg-slate-100 text-slate-700 text-[10px]">{REQ_LABEL[d.status] || d.status}</Badge>
        </div>
      ))}
    </div>
  );
};

const ParcelSummary = ({ parcel }) => {
  const open = OPEN.has(parcel.status) && !parcel.active_delivery;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3" data-testid="parcel-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-display font-bold text-lg">{parcel.pickup_location} → {parcel.drop_location}</div>
        <Badge className="bg-slate-900 text-white text-[10px]" data-testid="parcel-status">{(parcel.status || "").replace("_", " ").toUpperCase()}</Badge>
      </div>
      <Info icon={Package} testid="parcel-size">{sizeLabel(parcel)}{parcel.description ? ` · ${parcel.description}` : ""}</Info>
      <Info icon={MapPin}>{parcel.pickup_point?.address || parcel.pickup_location}<br/><span className="text-slate-500">to</span> {parcel.drop_point?.address || parcel.drop_location}{parcel.distance_km ? ` · ${Number(parcel.distance_km).toFixed(0)} km` : ""}</Info>
      {parcel.delivery_by && <Info icon={CalendarDays}>Deliver by {new Date(parcel.delivery_by).toLocaleDateString()}</Info>}
      <div className="flex gap-2 flex-wrap pt-1">
        {open && (
          <Button asChild className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="browse-travellers-btn">
            <Link to={`/parcel/${parcel.id}/browse`}><Users className="w-4 h-4 mr-1"/> Browse travellers</Link>
          </Button>
        )}
        {parcel.active_delivery?.contact && <ContactButtons delivery={parcel.active_delivery} role="sender"/>}
        {parcel.active_delivery && (
          <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="open-delivery-btn">
            <Link to={`/delivery/${parcel.active_delivery.id}`}>{parcel.active_delivery.status === "awaiting_payment" ? "Complete payment" : "Open delivery"}</Link>
          </Button>
        )}
      </div>
    </div>
  );
};

const ParcelPage = ({ browse = false }) => {
  const { id } = useParams();
  const nav = useNavigate();
  const [parcel, setParcel] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get(`/parcels/${id}`).then((r) => setParcel(r.data)).catch((e) => setError(e?.response?.data?.detail || "Parcel not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <TopBar/>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <button onClick={() => nav(browse ? `/parcel/${id}` : "/app")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="parcel-back-btn">
          <ArrowLeft className="w-4 h-4"/> {browse ? "Parcel details" : "Home"}
        </button>
        {error && <div className="rounded-2xl bg-white border p-8 text-center text-slate-500">{error}</div>}
        {!parcel && !error && <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>}
        {parcel && browse && <BrowseTrips parcel={parcel} onChanged={load}/>}
        {parcel && !browse && (
          <>
            <ParcelSummary parcel={parcel}/>
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Requests & offers</div>
              <RequestHistory items={parcel.deliveries || []}/>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ParcelPage;
